/**
 * Client portal billing actions — insurance updates via Medplum bot.
 */

import type { MedplumClient } from '@medplum/core';
import type { ClientPortalInsurancePriority } from '../utils/clientPortalFhirTypes';
import {
  saveClientPortalCoverageViaBot,
  type SaveClientPortalCoverageInput,
} from '../services/portalCoverageUpdateBot';

const NOT_IMPLEMENTED_MESSAGE =
  'Online payments are not connected yet. Your billing balance will be payable here once payment processing is enabled.';

export interface ClientPortalInsuranceUpdateInput {
  patientId: string;
  coverageId?: string;
  priority?: ClientPortalInsurancePriority;
  payerOrgId: string;
  payerName: string;
  memberId: string;
  groupNumber?: string;
  planName?: string;
  relationship?: string;
  subscriberFirstName?: string;
  subscriberLastName?: string;
  subscriberDateOfBirth?: string;
  periodStart?: string;
  periodEnd?: string;
  /** @deprecated Use priority instead. */
  episodeOfCareLabel?: string;
}

export class BillingActionNotImplementedError extends Error {
  constructor(message = NOT_IMPLEMENTED_MESSAGE) {
    super(message);
    this.name = 'BillingActionNotImplementedError';
  }
}

/** Initiates a client-portal payment for the given invoice (or current balance). */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export async function createClientPortalPaymentIntent(_invoiceId?: string): Promise<never> {
  throw new BillingActionNotImplementedError();
}

/** Initiates the payment method setup flow for the portal client. */
export async function createClientPortalPaymentMethodSetup(_episodeOfCareLabel?: string): Promise<never> {
  throw new BillingActionNotImplementedError();
}

/** Saves insurance details for a patient coverage slot via bot. */
export async function saveClientPortalInsurance(
  medplum: MedplumClient,
  input: ClientPortalInsuranceUpdateInput,
): Promise<void> {
  if (!input.patientId?.trim()) {
    throw new Error('Patient id is required to update insurance.');
  }
  if (!input.priority) {
    throw new Error('Plan priority is required.');
  }
  const payload: SaveClientPortalCoverageInput = {
    patientId: input.patientId,
    coverageId: input.coverageId,
    priority: input.priority,
    payerOrgId: input.payerOrgId,
    payerName: input.payerName,
    memberId: input.memberId,
    groupNumber: input.groupNumber,
    planName: input.planName,
    relationship: input.relationship,
    subscriberFirstName: input.subscriberFirstName,
    subscriberLastName: input.subscriberLastName,
    subscriberDateOfBirth: input.subscriberDateOfBirth,
    periodStart: input.periodStart,
    periodEnd: input.periodEnd,
  };
  await saveClientPortalCoverageViaBot(medplum, payload);
}

/** @deprecated Use saveClientPortalInsurance. */
export async function saveClientPortalInsuranceForEpisode(
  medplum: MedplumClient,
  input: ClientPortalInsuranceUpdateInput,
): Promise<void> {
  return saveClientPortalInsurance(medplum, input);
}

/** @deprecated Use saveClientPortalInsurance from the insurance edit modal. */
export async function updateClientPortalInsuranceForEpisode(
  _medplum: MedplumClient,
  _episodeOfCareLabel?: string,
): Promise<never> {
  throw new BillingActionNotImplementedError('Online insurance updates are not connected yet.');
}

/** Removes a saved payment method (placeholder). */
export async function removeClientPortalPaymentMethod(_paymentMethodId: string): Promise<never> {
  throw new BillingActionNotImplementedError();
}
